import {
  ActionIcon,
  Badge,
  Group,
  Image,
  Paper,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconDownload,
  IconFile,
  IconFileText,
  IconPhoto,
} from '@tabler/icons-react'

interface AttachmentPreviewProps {
  attachment: {
    id: number
    filename: string
    filesize: number
    mimeType: string
    downloadUrl: string
  }
}

export function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
  const isImage = attachment.mimeType.startsWith('image/')

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <IconPhoto size={16} />
    if (mimeType.includes('pdf')) return <IconFileText size={16} />
    return <IconFile size={16} />
  }

  const handleDownload = () => {
    window.open(attachment.downloadUrl, '_blank')
  }

  if (isImage) {
    return (
      <Stack gap="xs">
        <a
          href={attachment.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer' }}
        >
          <Paper p={0} withBorder style={{ overflow: 'hidden' }}>
            <Image
              src={attachment.downloadUrl}
              alt={attachment.filename}
              maw={300}
              radius="sm"
              fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f0f0f0'/%3E%3Ctext x='150' y='100' text-anchor='middle' dominant-baseline='middle' fill='%23999' font-family='Arial' font-size='14'%3ENo Preview%3C/text%3E%3C/svg%3E"
            />
          </Paper>
        </a>
        <Group justify="space-between" gap="xs" px="xs">
          <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
            {attachment.filename}
          </Text>
          <Badge size="xs" variant="outline">
            {formatFileSize(attachment.filesize)}
          </Badge>
        </Group>
      </Stack>
    )
  }

  return (
    <Paper p="xs" withBorder>
      <Group justify="space-between" gap="xs">
        <Group gap="xs">
          {getFileIcon(attachment.mimeType)}
          <Text size="sm" lineClamp={1}>
            {attachment.filename}
          </Text>
          <Badge size="sm" variant="outline">
            {formatFileSize(attachment.filesize)}
          </Badge>
        </Group>

        <ActionIcon
          size="sm"
          variant="subtle"
          onClick={handleDownload}
          aria-label="Download attachment"
        >
          <IconDownload size={14} />
        </ActionIcon>
      </Group>
    </Paper>
  )
}
